import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  MeetingRoom,
  Today,
} from '@mui/icons-material';
import { useGetLocations } from '../hooks/reactQuerys/LocationQueries';
import { useGetRoomOccupancy } from '../hooks/reactQuerys/RoomQueries';
import { ILocation } from '../Interfaces/ILocation';
import { IRoomBooking } from '../Interfaces/IRoom';
import { castWithZeroTimezone } from '../core/generic_helper';

// Tages-Zeitachse 07:00–20:00.
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 20;
const DAY_START_MIN = DAY_START_HOUR * 60;
const TOTAL_MIN = (DAY_END_HOUR - DAY_START_HOUR) * 60;

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const pad = (n: number) => String(n).padStart(2, '0');
const toYMD = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
// Montag der Woche zu einem Datum.
const startOfWeek = (d: Date) => {
  const x = new Date(d);
  const weekday = (x.getDay() + 6) % 7; // Mo=0 … So=6
  x.setDate(x.getDate() - weekday);
  x.setHours(0, 0, 0, 0);
  return x;
};

const bookingStart = (b: IRoomBooking) => castWithZeroTimezone(b.Meeting_at);
const bookingEnd = (b: IRoomBooking) =>
  new Date(bookingStart(b).getTime() + (b.DurationInMin ?? 0) * 60000);
const fmtTime = (d: Date) =>
  d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
const bookingLabel = (b: IRoomBooking) =>
  `${fmtTime(bookingStart(b))}–${fmtTime(bookingEnd(b))} · ${b.Title}`;

type ViewMode = 'day' | 'week';

/** Belegungsplan: zeigt je Raum eines Standorts die gebuchten Termine –
 *  als Tages-Zeitachse oder Wochenübersicht. Liest die bereits gespeicherten
 *  Vorgangs-Termine (LegalTransactionMeeting mit zugewiesenem Raum). */
export const RoomOccupancyPlan: React.FC = () => {
  const { locations = [] } = useGetLocations();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [anchorDate, setAnchorDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [viewMode, setViewMode] = useState<ViewMode>('day');

  // Effektiver Standort: gewählter, sonst Standardstandort, sonst erster.
  const effectiveLocationId =
    selectedId ??
    locations.find(l => l.isDefault)?.idLocation ??
    locations[0]?.idLocation ??
    null;
  const selectedLocation =
    locations.find(l => l.idLocation === effectiveLocationId) ?? null;

  const { dateFrom, dateTo, days } = useMemo(() => {
    if (viewMode === 'day') {
      const ymd = toYMD(anchorDate);
      return { dateFrom: ymd, dateTo: ymd, days: [new Date(anchorDate)] };
    }
    const monday = startOfWeek(anchorDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
    return {
      dateFrom: toYMD(monday),
      dateTo: toYMD(addDays(monday, 6)),
      days: weekDays,
    };
  }, [viewMode, anchorDate]);

  const { occupancy, isLoadingOccupancy } = useGetRoomOccupancy(
    effectiveLocationId,
    dateFrom,
    dateTo
  );

  const step = viewMode === 'day' ? 1 : 7;
  const rangeLabel =
    viewMode === 'day'
      ? anchorDate.toLocaleDateString('de-DE', {
          weekday: 'long',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : `${days[0].toLocaleDateString('de-DE')} – ${days[6].toLocaleDateString('de-DE')}`;

  return (
    <Box>
      {/* Steuerleiste */}
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        flexWrap='wrap'
        sx={{ mb: 2 }}
      >
        <Autocomplete
          sx={{ minWidth: 240 }}
          options={locations}
          value={selectedLocation}
          getOptionLabel={(o: ILocation) =>
            o.Location || [o.Street, o.StreetNr].filter(Boolean).join(' ')
          }
          isOptionEqualToValue={(o, v) => o.idLocation === v.idLocation}
          onChange={(_, v) => setSelectedId(v?.idLocation ?? null)}
          renderInput={params => (
            <TextField {...params} label='Standort' size='small' />
          )}
        />

        <ToggleButtonGroup
          size='small'
          exclusive
          value={viewMode}
          onChange={(_, value) => value && setViewMode(value)}
        >
          <ToggleButton value='day'>Tag</ToggleButton>
          <ToggleButton value='week'>Woche</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction='row' alignItems='center' spacing={0.5}>
          <Tooltip title='Zurück'>
            <IconButton
              size='small'
              onClick={() => setAnchorDate(addDays(anchorDate, -step))}
            >
              <ChevronLeft />
            </IconButton>
          </Tooltip>
          <Tooltip title='Heute'>
            <IconButton
              size='small'
              onClick={() => {
                const d = new Date();
                d.setHours(0, 0, 0, 0);
                setAnchorDate(d);
              }}
            >
              <Today fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Weiter'>
            <IconButton
              size='small'
              onClick={() => setAnchorDate(addDays(anchorDate, step))}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography variant='subtitle1' sx={{ fontWeight: 500 }}>
          {rangeLabel}
        </Typography>
      </Stack>

      {effectiveLocationId === null ? (
        <Typography color='text.secondary'>
          Bitte einen Standort wählen.
        </Typography>
      ) : isLoadingOccupancy ? (
        <Typography color='text.secondary'>Belegung wird geladen…</Typography>
      ) : occupancy.length === 0 ? (
        <Typography color='text.secondary'>
          Für diesen Standort sind keine Räume hinterlegt.
        </Typography>
      ) : viewMode === 'day' ? (
        <DayView occupancy={occupancy} />
      ) : (
        <WeekView occupancy={occupancy} days={days} />
      )}
    </Box>
  );
};

/** Tagesansicht: Räume als Zeilen, Zeitachse 07:00–20:00, Termine als Blöcke. */
const DayView: React.FC<{
  occupancy: {
    idRoom: number;
    Name: string;
    Capacity: number | null;
    bookings: IRoomBooking[];
  }[];
}> = ({ occupancy }) => {
  const hours = Array.from(
    { length: DAY_END_HOUR - DAY_START_HOUR + 1 },
    (_, i) => DAY_START_HOUR + i
  );

  return (
    <Paper variant='outlined' sx={{ overflowX: 'auto' }}>
      {/* Stunden-Kopf */}
      <Box
        sx={{
          display: 'flex',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ width: 180, flexShrink: 0, p: 1 }} />
        <Box sx={{ position: 'relative', flex: 1, height: 24, minWidth: 520 }}>
          {hours.map(h => (
            <Box
              key={h}
              sx={{
                position: 'absolute',
                left: `${((h * 60 - DAY_START_MIN) / TOTAL_MIN) * 100}%`,
                transform: 'translateX(-50%)',
                fontSize: 11,
                color: 'text.secondary',
              }}
            >
              {pad(h)}:00
            </Box>
          ))}
        </Box>
      </Box>

      {occupancy.map(room => (
        <Box
          key={room.idRoom}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          <Box
            sx={{
              width: 180,
              flexShrink: 0,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <MeetingRoom fontSize='small' sx={{ color: 'text.disabled' }} />
            <Box>
              <Typography variant='body2' noWrap>
                {room.Name}
              </Typography>
              {room.Capacity != null && (
                <Typography variant='caption' color='text.secondary'>
                  {room.Capacity} Pers.
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minWidth: 520,
              minHeight: 44,
              backgroundImage:
                'repeating-linear-gradient(90deg, rgba(0,0,0,0.06) 0, rgba(0,0,0,0.06) 1px, transparent 1px, transparent calc(100%/13))',
            }}
          >
            {room.bookings.map(booking => {
              const start = bookingStart(booking);
              const startMin = start.getHours() * 60 + start.getMinutes();
              const endMin = startMin + (booking.DurationInMin ?? 0);
              const clampStart = Math.max(startMin, DAY_START_MIN);
              const clampEnd = Math.min(endMin, DAY_END_HOUR * 60);
              if (clampEnd <= clampStart) return null;
              const left = ((clampStart - DAY_START_MIN) / TOTAL_MIN) * 100;
              const width = ((clampEnd - clampStart) / TOTAL_MIN) * 100;
              return (
                <Tooltip
                  key={booking.idLegalTransactionMeeting}
                  title={bookingLabel(booking)}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      bottom: 4,
                      left: `${left}%`,
                      width: `${width}%`,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: 1,
                      px: 0.5,
                      fontSize: 11,
                      lineHeight: '1.2',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {fmtTime(start)} {booking.Title}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

/** Wochenansicht: Räume als Zeilen, Mo–So als Spalten, Termine je Zelle gelistet. */
const WeekView: React.FC<{
  occupancy: {
    idRoom: number;
    Name: string;
    Capacity: number | null;
    bookings: IRoomBooking[];
  }[];
  days: Date[];
}> = ({ occupancy, days }) => {
  return (
    <Paper variant='outlined' sx={{ overflowX: 'auto' }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 160 }}>Raum</TableCell>
            {days.map((d, i) => (
              <TableCell key={toYMD(d)} sx={{ minWidth: 120 }}>
                {WEEKDAYS[i]}{' '}
                {d.toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                })}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {occupancy.map(room => (
            <TableRow key={room.idRoom}>
              <TableCell>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <MeetingRoom
                    fontSize='small'
                    sx={{ color: 'text.disabled' }}
                  />
                  <span>{room.Name}</span>
                  {room.Capacity != null && (
                    <Typography variant='caption' color='text.secondary'>
                      ({room.Capacity})
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              {days.map(d => {
                const key = toYMD(d);
                const dayBookings = room.bookings
                  .filter(b => toYMD(bookingStart(b)) === key)
                  .sort(
                    (a, b) =>
                      bookingStart(a).getTime() - bookingStart(b).getTime()
                  );
                return (
                  <TableCell key={key} sx={{ verticalAlign: 'top' }}>
                    <Stack spacing={0.5}>
                      {dayBookings.map(b => (
                        <Tooltip
                          key={b.idLegalTransactionMeeting}
                          title={bookingLabel(b)}
                        >
                          <Chip
                            size='small'
                            color='primary'
                            variant='outlined'
                            label={`${fmtTime(bookingStart(b))} ${b.Title}`}
                            sx={{ maxWidth: 160, justifyContent: 'flex-start' }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};
